import React from 'react';
import { Typography, Box } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { SIDEBAR_SECTIONS, SectionKey } from '../data/sectionSidebarConfig';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import CollectionsIcon from '@mui/icons-material/Collections';
import CampaignIcon from '@mui/icons-material/Campaign';
import TempleHinduIcon from '@mui/icons-material/TempleHindu';
import ExploreIcon from '@mui/icons-material/Explore';
import ContactMailIcon from '@mui/icons-material/ContactMail';

interface SectionSidebarProps {
  sectionKey: SectionKey;
  activePath: string;
}

/* ── theme config per section ── */
const SECTION_THEME: Record<string, { gradient: [string, string]; icon: React.ElementType }> = {
  poojas:  { gradient: ['#ef4444', '#dc2626'], icon: LocalFireDepartmentIcon },
  donate:  { gradient: ['#ec4899', '#db2777'], icon: VolunteerActivismIcon },
  gallery: { gradient: ['#8b5cf6', '#7c3aed'], icon: CollectionsIcon },
  news:    { gradient: ['#3b82f6', '#2563eb'], icon: CampaignIcon },
  about:   { gradient: ['#f59e0b', '#d97706'], icon: TempleHinduIcon },
  nearby:  { gradient: ['#22c55e', '#16a34a'], icon: ExploreIcon },
  contact: { gradient: ['#06b6d4', '#0891b2'], icon: ContactMailIcon },
};

export default function SectionSidebar({ sectionKey, activePath }: SectionSidebarProps) {
  const config = SIDEBAR_SECTIONS[sectionKey];
  if (!config) return null;

  const theme = SECTION_THEME[sectionKey] || SECTION_THEME.nearby;
  const [gStart, gEnd] = theme.gradient;
  const SIcon = theme.icon;

  return (
    <Box sx={{
      position: { md: 'sticky' }, top: { md: 100 },
      borderRadius: '24px', overflow: 'hidden',
      bgcolor: '#fff',
      border: '1px solid rgba(0,0,0,0.06)',
      boxShadow: '0 12px 48px rgba(0,0,0,0.06)',
    }}>

      {/* ── Vibrant Gradient Header ── */}
      <Box sx={{
        background: `linear-gradient(135deg, ${gStart}, ${gEnd})`,
        px: 2.5, py: 3,
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Dot pattern */}
        <Box sx={{
          position: 'absolute', inset: 0, opacity: 0.06,
          backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
          backgroundSize: '16px 16px',
        }} />
        {/* Decorative circle */}
        <Box sx={{
          position: 'absolute', top: -30, right: -30,
          width: 100, height: 100, borderRadius: '50%',
          background: 'rgba(255,255,255,0.1)',
        }} />
        <Box sx={{
          position: 'absolute', bottom: -20, left: -20,
          width: 70, height: 70, borderRadius: '50%',
          background: 'rgba(255,255,255,0.06)',
        }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: '16px',
            background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1.5px solid rgba(255,255,255,0.25)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          }}>
            <SIcon sx={{ fontSize: 24, color: '#fff' }} />
          </Box>
          <Box>
            <Typography className="notranslate" translate="no" sx={{
              fontWeight: 800, fontSize: { xs: '0.9rem', md: '1.05rem' }, color: '#fff', lineHeight: 1.2,
              textShadow: '0 1px 8px rgba(0,0,0,0.15)',
            }}>
              {config.title}
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)', mt: 0.3, fontWeight: 500 }}>
              Quick Navigation
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Description ── */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        <Typography sx={{ fontSize: '0.76rem', color: '#94a3b8', lineHeight: 1.75 }}>
          {config.description}
        </Typography>
      </Box>

      {/* ── Divider ── */}
      <Box sx={{ mx: 2.5, height: '1px', background: `linear-gradient(90deg, transparent, ${gStart}25, transparent)` }} />

      {/* ── Navigation Links ── */}
      <Box className="notranslate" translate="no" sx={{ px: 1.5, py: 1.5 }}>
        {config.links.map((link) => {
          const isActive = link.to === activePath;
          return (
            <Box
              key={link.to}
              component={RouterLink}
              to={link.to}
              sx={{
                display: 'flex', alignItems: 'center',
                px: 1.8, py: 1.3,
                mb: 0.5,
                borderRadius: '12px',
                textDecoration: 'none',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',

                ...(isActive ? {
                  background: `linear-gradient(135deg, ${gStart}12, ${gEnd}08)`,
                  border: `1px solid ${gStart}20`,
                  boxShadow: `0 2px 12px ${gStart}10`,
                } : {
                  border: '1px solid transparent',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.02)',
                    transform: 'translateX(4px)',
                    '& .navArrow': { opacity: 1, transform: 'translateX(0)' },
                    '& .navDot': { background: `${gStart}40` },
                  },
                }),
              }}
            >
              {/* Number badge / indicator */}
              <Box
                className="navDot"
                sx={{
                  width: 28, height: 28,
                  borderRadius: '9px', flexShrink: 0, mr: 1.2,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: isActive
                    ? `linear-gradient(135deg, ${gStart}, ${gEnd})`
                    : 'rgba(0,0,0,0.04)',
                  boxShadow: isActive ? `0 4px 14px ${gStart}30` : 'none',
                  transition: 'all 300ms ease',
                }}
              >
                {isActive ? (
                  <NavigateNextRoundedIcon sx={{ fontSize: 16, color: '#fff' }} />
                ) : (
                  <Box sx={{
                    width: 6, height: 6, borderRadius: '50%',
                    bgcolor: 'rgba(0,0,0,0.12)',
                    transition: 'all 300ms ease',
                  }} />
                )}
              </Box>

              <Typography sx={{
                flex: 1,
                fontSize: '0.82rem',
                fontWeight: isActive ? 750 : 550,
                color: isActive ? gStart : '#64748b',
                transition: 'color 300ms ease',
                letterSpacing: isActive ? '0.01em' : 0,
              }}>
                {link.label}
              </Typography>

              <NavigateNextRoundedIcon
                className="navArrow"
                sx={{
                  fontSize: 16,
                  color: isActive ? gStart : '#cbd5e1',
                  opacity: isActive ? 1 : 0,
                  transform: isActive ? 'translateX(0)' : 'translateX(-6px)',
                  transition: 'all 300ms ease',
                }}
              />
            </Box>
          );
        })}
      </Box>

      {/* ── Tip Box ── */}
      {config.tip && (
        <Box sx={{ px: 2, pb: 2.5, pt: 0.5 }}>
          <Box sx={{
            px: 2, py: 2,
            borderRadius: '14px',
            background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 100%)',
            position: 'relative', overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.05)',
          }}>
            <Box sx={{
              position: 'absolute', inset: 0, opacity: 0.03,
              backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
              backgroundSize: '12px 12px',
            }} />
            <Box sx={{
              position: 'absolute', top: 0, left: 0, right: 0, height: 2,
              background: `linear-gradient(90deg, transparent, ${gStart}80, transparent)`,
            }} />

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2, position: 'relative', zIndex: 2 }}>
              <Box sx={{
                width: 26, height: 26, borderRadius: '8px', flexShrink: 0, mt: 0.2,
                background: `linear-gradient(135deg, ${gStart}, ${gEnd})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 4px 12px ${gStart}30`,
              }}>
                <TipsAndUpdatesOutlinedIcon sx={{ fontSize: 14, color: '#fff' }} />
              </Box>
              <Typography sx={{
                fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)',
                lineHeight: 1.65, fontWeight: 500,
              }}>
                {config.tip}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
